import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, PlayCircle, Mic, Eye, BookOpen, ChevronRight, Star, Globe, Heart, Volume2, HelpCircle, Menu, X, LogIn, RotateCcw, Video } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import WhatsAppGlyph from "../components/WhatsAppGlyph";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import PoemTile from "../components/PoemTile";
import BigButton from "../components/BigButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import MicroOnboarding from "../components/MicroOnboarding";
import UnlockCountdownDialog from "../components/UnlockCountdownDialog";
import PoemCoverCarousel from "../components/PoemCoverCarousel";
import WhatsAppButton from "../components/WhatsAppButton";
import LiveClassCard from "../components/LiveClassCard";
import WhatsAppCommunityCard, { WHATSAPP_COMMUNITY_URL } from "../components/WhatsAppCommunityCard";
import GuidedTour from "../components/tour/GuidedTour";
import useTour from "../hooks/useTour";
import landingTourSteps from "../constants/tours/landingTour";
import { TOUR } from "../constants/testIds/tour";
import { IconByName } from "../lib/wordIcon";

// Feature cards inspired by the reference: Sing / Recognise / Remember
const FEATURES = [
    { icon: "Mic", title: "Sing", desc: "Watch, listen, and sing along to short poems built for tiny voices.", bg: "bg-peach", iconWrap: "bg-white" },
    { icon: "Eye", title: "Recognise", desc: "Tap picture flashcards to link each word to a soft, friendly icon.", bg: "bg-pastelYellow", iconWrap: "bg-white" },
    { icon: "Brain", title: "Remember", desc: "A gentle picture quiz repeats the words until they stick.", bg: "bg-sky", iconWrap: "bg-white" },
];

const STATS = [
    { icon: "BookOpen", value: "20", label: "hand-picked poems" },
    { icon: "Globe", value: "10", label: "languages" },
    { icon: "ShieldCheck", value: "0", label: "ads or trackers" },
    { icon: "Heart", value: "3–10", label: "years old" },
];

const WALKTHROUGH = [
    { step: "1", title: "Watch a short poem", desc: "A gentle video and soft audio version they can loop as many times as they like.", icon: "PlayCircle" },
    { step: "2", title: "Tap the word cards", desc: "Each poem has 4–6 picture flashcards. Tap one, hear it, tap it again.", icon: "Volume2" },
    { step: "3", title: "Play the picture quiz", desc: "Three choices, one right answer. A soft chime for every star earned.", icon: "HelpCircle" },
    { step: "4", title: "Sing and record", desc: "Their voice, private to your family — only you can play it back.", icon: "Mic" },
];

const FAQS = [
    { q: "Is Little Verses really free?", a: "Yes \u2014 free for the first 1,000 users, no payment button anywhere in the product. It\u2019s a passion project from the HikmahVerses team." },
    { q: "How is my child\u2019s privacy protected?", a: "Children never sign in on their own. Everything happens under a guardian\u2019s account. Every recording is private by default \u2014 there\u2019s no public gallery, no comments, and no messaging between children. Ever." },
    { q: "What ages is it for?", a: "Built for pre-readers (3\u20136) and early readers (7\u201310). You pick an age band when you add each child, so their profile reflects it \u2014 the same 20 poems, word cards, and quizzes work across both bands." },
    { q: "Which languages will you translate to?", a: "Poem transcripts and word meanings work in English, Arabic (RTL), Urdu (RTL), Hindi, French, Spanish, Turkish, Malay, Indonesian, and Bengali. Videos and audio stay in English \u2014 translations show as subtitles and card meanings." },
    { q: "How does this relate to the Quran Fluency (QFC) app?", a: "Same family \u2014 HikmahVerses / Verses Learning Hub. When we launch cross-app sync, one account will follow your child across both products." },
    { q: "Do you use AI voices or real recordings?", a: "The 20 curated poems use real spoken narration. When your child sings and records, we save their voice as-is \u2014 never analysed, never used for training." },
];

const LEARNER_KEY = "lv.activeLearner";

export default function Landing() {
    const navigate = useNavigate();
    const { user, loading, logout } = useAuth();
    const { lang } = useLang();
    const [poems, setPoems] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [lockedDialog, setLockedDialog] = useState(null); // {title, subtitle, unlocksAt} | null
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    // Public, no-auth endpoint — this page renders for signed-out visitors
    // too, so it can't ride on the signed-in user.live_class the way
    // Dashboard.jsx does. Same shared shape either way (_live_class_public
    // on the backend); see LiveClassCard.jsx.
    const [liveClass, setLiveClass] = useState(null);
    // Only meaningful while signed in — see AuthContext.logout for why a
    // stale id can otherwise linger in localStorage after signing out.
    const learnerId = user ? (localStorage.getItem(LEARNER_KEY) || "") : "";
    const tour = useTour("landing");

    // Show on every visitor's first-ever visit to this page (useTour's
    // "completed" flag is what stops it from showing again afterwards).
    useEffect(() => {
        tour.start();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/poems", { params: { lang, learner_id: learnerId || undefined } });
                setPoems(data.poems || []);
            } finally {
                setFetching(false);
            }
        })();
    }, [user, lang, learnerId]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/settings/live-class");
                setLiveClass(data);
            } catch {
                setLiveClass({ enabled: false });
            }
        })();
    }, []);

    const goPoem = (p) => {
        if (p.locked) {
            if (!user) {
                window.scrollTo({ top: 0, behavior: "smooth" });
                toast("Sign in to see poems", { description: "Create a free account to start reading with your child." });
            } else if (p.lock_reason === "child_required") {
                toast("Add a child to continue", { description: "Head to your dashboard to create or select a child, then this poem opens." });
            } else if (p.unlocks_at) {
                setLockedDialog({ title: `Poem #${String(p.order_index).padStart(2, "0")}`, subtitle: p.title, unlocksAt: p.unlocks_at });
            } else {
                toast("Not unlocked yet", { description: "Finish the current poem's week first — this one opens after." });
            }
            return;
        }
        if (p.is_coming_soon) return;
        navigate(`/poem/${p.poem_id}`);
    };

    const playFirst = () => {
        const first = poems.find((p) => p.is_free_preview) || poems[0];
        if (first) navigate(`/poem/${first.poem_id}`);
    };

    return (
        <div className="min-h-screen">
            {/* Sticky header */}
            <header className="sticky top-0 z-40 bg-cream/90 backdrop-blur border-b border-honeySoft" data-testid="landing-header">
                {/* flex-wrap — at sm-to-md widths, the nav links plus every
                    action button (How to use / language / sign-in-or-
                    dashboard / play) genuinely don't all fit on one line in
                    this container's max-w-6xl. Without flex-wrap AND
                    shrink-0 on each pill below, flex's default shrink-to-fit
                    behavior squeezed the buttons themselves narrower than
                    their own label, wrapping the text INSIDE each pill
                    ("How to" / "use" stacked) instead of letting the whole
                    row wrap onto a second line — confirmed live via a
                    screenshot at ~1150px wide. */}
                <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4 gap-2 flex-wrap">
                    <button onClick={() => navigate("/")} className="flex items-center gap-3 shrink-0">
                        <img src="/brand/hikmahverses-mark.png" alt="HikmahVerses" className="h-9 w-auto shrink-0" />
                        <div className="text-left">
                            <p className="font-display font-bold text-lg leading-none text-deepGreen">Little Verses</p>
                            <p className="text-[11px] text-mocha">by HikmahVerses</p>
                        </div>
                    </button>
                    <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-deepGreen">
                        <a href="#library" className="hover:text-brightGreen">Poems</a>
                        <a href="#how" className="hover:text-brightGreen">How it works</a>
                        <a href="#faq" className="hover:text-brightGreen">FAQ</a>
                        <a href="#promise" className="hover:text-brightGreen">Promise</a>
                    </nav>
                    <div className="flex items-center justify-end flex-wrap gap-1.5 sm:gap-2">
                        {/* Opens the admin-uploaded help guides. Public on
                            purpose — a visitor who hasn't signed up yet is
                            exactly who needs "how do I use this?" most.
                            shrink-0 + whitespace-nowrap on every pill in
                            this group — without them flex's default
                            shrink-to-fit let the pills themselves get
                            squeezed narrower than their label, wrapping
                            the text INSIDE the pill instead of the group
                            wrapping as a whole (confirmed live). */}
                        <button
                            onClick={() => navigate("/how-to-use")}
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 font-display font-semibold text-deepGreen hover:bg-honeySoft h-10 text-sm shrink-0 whitespace-nowrap"
                            data-testid="nav-how-to-use"
                        >
                            <HelpCircle className="w-4 h-4 shrink-0" strokeWidth={2.6} />
                            How to use
                        </button>
                        <LanguageSwitcher compact />
                        {user ? (
                            <>
                                <button onClick={() => navigate("/dashboard")} className="hidden sm:inline-flex items-center justify-center rounded-full bg-white px-4 py-2 font-display font-semibold text-deepGreen hover:bg-honeySoft h-10 text-sm shrink-0 whitespace-nowrap" data-testid="nav-dashboard">
                                    Dashboard
                                </button>
                                <button onClick={logout} className="hidden sm:inline-flex items-center justify-center rounded-full px-3 py-2 font-display font-semibold text-mocha hover:bg-white h-10 text-sm shrink-0 whitespace-nowrap" data-testid="nav-logout">
                                    Sign out
                                </button>
                            </>
                        ) : (
                            // "Play the first rhyme" used to sit here too —
                            // removed (confirmed live via a screenshot: it
                            // was the widest pill in an already-crowded
                            // row, and at sm-to-md widths it wrapped onto
                            // its own line with an ugly gap around it).
                            // Redundant anyway — the hero just below always
                            // has its own, unmissable copy of this exact
                            // button, so nothing is actually lost.
                            <button onClick={() => navigate("/login")} className="hidden sm:inline-flex items-center justify-center rounded-full bg-white px-4 py-2 font-display font-semibold text-deepGreen hover:bg-honeySoft h-10 text-sm shrink-0 whitespace-nowrap" data-testid="nav-signin">
                                Sign in
                            </button>
                        )}
                        {/* Mobile-only hamburger — the nav links and the
                            sign-in/dashboard actions above are both hidden
                            below md, so this is the only way to reach them
                            on a phone. */}
                        <button
                            type="button"
                            onClick={() => setMobileNavOpen((v) => !v)}
                            className="md:hidden rounded-full p-2.5 text-deepGreen hover:bg-white min-h-[40px] min-w-[40px] flex items-center justify-center"
                            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileNavOpen}
                            data-testid="nav-mobile-toggle"
                        >
                            {mobileNavOpen ? <X className="w-5 h-5" strokeWidth={2.6} /> : <Menu className="w-5 h-5" strokeWidth={2.6} />}
                        </button>
                    </div>
                </div>

                {mobileNavOpen && (
                    <div className="md:hidden border-t border-honeySoft bg-cream px-4 pb-4 pt-2" data-testid="nav-mobile-panel">
                        <nav className="flex flex-col text-sm font-semibold text-deepGreen">
                            {[
                                { href: "#library", label: "Poems" },
                                { href: "#how", label: "How it works" },
                                { href: "#faq", label: "FAQ" },
                                { href: "#promise", label: "Promise" },
                            ].map((l) => (
                                <a key={l.href} href={l.href} onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-honeySoft/70 last:border-0">
                                    {l.label}
                                </a>
                            ))}
                            {/* The desktop "How to use" button is hidden below
                                sm, so it needs its own entry here to stay
                                reachable on a phone. */}
                            <button
                                type="button"
                                onClick={() => { setMobileNavOpen(false); navigate("/how-to-use"); }}
                                className="py-3 text-left border-t border-honeySoft/70 inline-flex items-center gap-2"
                                data-testid="nav-mobile-how-to-use"
                            >
                                <HelpCircle className="w-4 h-4 shrink-0" strokeWidth={2.6} />
                                How to use
                            </button>
                        </nav>
                        <div className="flex flex-col gap-2 mt-3">
                            {user ? (
                                <>
                                    <button onClick={() => { setMobileNavOpen(false); navigate("/dashboard"); }} className="rounded-full bg-white px-4 py-2.5 font-display font-semibold text-deepGreen hover:bg-honeySoft min-h-[44px] text-sm" data-testid="nav-mobile-dashboard">
                                        Dashboard
                                    </button>
                                    <button onClick={() => { setMobileNavOpen(false); logout(); }} className="rounded-full px-4 py-2.5 font-display font-semibold text-mocha hover:bg-white min-h-[44px] text-sm" data-testid="nav-mobile-logout">
                                        Sign out
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => { setMobileNavOpen(false); playFirst(); }} className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-white px-5 py-2.5 font-display font-semibold hover:bg-terracottaDark min-h-[44px] text-sm shadow-md shadow-terracotta/30" data-testid="nav-mobile-play-first">
                                        <PlayCircle className="w-4 h-4" strokeWidth={2.6} />
                                        Play the first rhyme
                                    </button>
                                    <button onClick={() => { setMobileNavOpen(false); navigate("/login"); }} className="rounded-full bg-white px-4 py-2.5 font-display font-semibold text-deepGreen hover:bg-honeySoft min-h-[44px] text-sm" data-testid="nav-mobile-signin">
                                        Sign in
                                    </button>
                                    <button onClick={() => { setMobileNavOpen(false); navigate("/register"); }} className="rounded-full px-4 py-2.5 font-display font-semibold text-mocha hover:bg-white min-h-[44px] text-sm" data-testid="nav-mobile-register">
                                        Create an account
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                {/* Decorative Arabic script watermark */}
                <div aria-hidden className="pointer-events-none select-none absolute inset-0 opacity-[0.06]">
                    <p className="font-arabic text-deepGreen text-[16rem] leading-none absolute -top-10 -right-10">نور</p>
                    <p className="font-arabic text-deepGreen text-[10rem] leading-none absolute bottom-10 -left-10">سلام</p>
                </div>

                <div className="max-w-6xl mx-auto px-6 pt-6 relative">
                </div>

                {/* Client requirement, 2026-09-25 (Dr. Mohamed Ibrahim,
                    CEO): "remove live session and community link in the
                    top and bring it as an icon or link in the footer" —
                    this row (LiveClassCard + WhatsAppCommunityCard) used to
                    sit right here, above the headline. Both are now footer
                    icon links instead (see the Footer section below). */}

                <div className="max-w-6xl mx-auto px-6 pt-8 pb-14 sm:pt-8 sm:pb-20 relative">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-center">
                        <div className="md:col-span-3 space-y-6">
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-deepGreen">
                                Twenty tiny poems.<br />
                                <span className="text-brightGreen">One giggly grown-up</span> reading along.
                            </h1>
                            <p className="text-lg sm:text-xl text-mocha max-w-xl leading-relaxed">
                                A gentle library of English poems for ages 3 to 10. Watch a poem, tap the word cards, take the picture quiz, then sing it back — all in one cozy place.
                            </p>
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.4 }}
                                className="inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-5 py-2.5 shadow-md shadow-terracotta/20"
                                data-testid="landing-free-users-badge"
                            >
                                <Sparkles className="w-4 h-4 shrink-0" strokeWidth={2.8} />
                                <p className="font-display font-bold text-sm sm:text-base">Free for the first 1,000 users</p>
                            </motion.div>
                            <span className="inline-flex items-center gap-2 rounded-full bg-honeySoft px-4 py-1.5 text-sm font-bold text-deepGreen">
                                <BookOpen className="w-4 h-4 shrink-0" strokeWidth={2.8} />
                                <span>20 weeks</span>
                                <span className="text-brightGreen">·</span>
                                <span>20 poems</span>
                            </span>

                            <div className="flex flex-wrap items-center gap-4" id="signin-cta">
                                <BigButton onClick={playFirst} variant="primary" data-testid="hero-play-first">
                                    <PlayCircle className="w-5 h-5" strokeWidth={2.5} />
                                    Play the first rhyme
                                </BigButton>
                                {user ? (
                                    <button onClick={() => navigate("/dashboard")} className="rounded-full px-6 py-3 font-display font-semibold text-deepGreen underline decoration-brightGreen decoration-4 underline-offset-4 hover:decoration-terracotta min-h-[52px]" data-testid="hero-dashboard">
                                        Open my dashboard
                                    </button>
                                ) : (
                                    <BigButton onClick={() => navigate("/login")} variant="secondary" data-testid="hero-signin">
                                        <LogIn className="w-5 h-5" strokeWidth={2.5} />
                                        Sign in to unlock all 20
                                    </BigButton>
                                )}
                            </div>
                            <div className="flex items-center gap-6 pt-2 text-sm text-mocha flex-wrap">
                                <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brightGreen" strokeWidth={2.8} /> Recordings stay private by default</span>
                                <span className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-brightGreen" strokeWidth={2.8} /> Ages 3–10</span>
                            </div>
                        </div>

                        {/* Video-like preview card */}
                        <div className="md:col-span-2">
                            <motion.div
                                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 100, damping: 14 }}
                                className="relative rounded-[2.5rem] bg-white p-4 shadow-xl shadow-deepGreen/10 border border-honeySoft"
                            >
                                <PoemCoverCarousel poems={poems} />
                                <motion.div
                                    animate={{ rotate: [0, 10, -6, 0] }}
                                    transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                                    className="absolute -top-5 -right-4 rounded-full bg-brightGreen p-3.5 shadow-md"
                                >
                                    <Sparkles className="w-5 h-5 text-white" strokeWidth={2.8} />
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats trust bar */}
            <section className="bg-white border-y border-honeySoft">
                <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="stats-bar">
                    {STATS.map((s) => (
                        <div key={s.label} className="flex items-center gap-3">
                            <div className="rounded-2xl bg-honeySoft p-2.5">
                                <IconByName name={s.icon} className="w-5 h-5 text-brightGreen" strokeWidth={2.5} />
                            </div>
                            <div>
                                <p className="font-display text-2xl font-bold text-deepGreen leading-none">{s.value}</p>
                                <p className="text-sm text-mocha">{s.label}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Micro-onboarding: silent auto-preview of poem #01 */}
            <MicroOnboarding />

            {/* "20 Poems Total" badge stripe */}
            <section className="bg-brightGreen text-white line-pattern">
                <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="rounded-2xl bg-white/20 p-2.5">
                            <BookOpen className="w-5 h-5" strokeWidth={2.8} />
                        </div>
                        <p className="font-display text-2xl font-bold">20 Poems Total</p>
                    </div>
                    <p className="text-white/90 text-sm sm:text-base max-w-md">The first is open for anyone to try. The rest unlock the moment a grown-up signs in — always free.</p>
                </div>
            </section>

            {/* How it works — 3 pastel cards */}
            <section id="how" className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <p className="font-display text-sm font-bold text-brightGreen uppercase tracking-wider">How a poem works</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-deepGreen mt-2">Start with words they can say today</h2>
                    <p className="text-mocha mt-3">Every poem walks a child through three tiny wins — no reading required.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ y: 12, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.12 }}
                            className={`rounded-[2rem] p-8 ${f.bg} border border-white`}
                        >
                            <div className={`rounded-2xl ${f.iconWrap} p-3 inline-block shadow-sm`}>
                                <IconByName name={f.icon} className="w-7 h-7 text-deepGreen" strokeWidth={2.4} />
                            </div>
                            <p className="font-display text-2xl font-bold text-deepGreen mt-6">{f.title}</p>
                            <p className="text-mocha mt-2 leading-relaxed">{f.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Walkthrough — 4 numbered steps */}
            <section className="max-w-6xl mx-auto px-6 pb-4">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <p className="font-display text-sm font-bold text-brightGreen uppercase tracking-wider">Every poem, four tiny steps</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-deepGreen mt-2">Two minutes. A whole new word.</h2>
                    <p className="text-mocha mt-3">A predictable rhythm children come to trust — the same shape for every poem in the library.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="walkthrough">
                    {WALKTHROUGH.map((w, i) => (
                        <motion.div
                            key={w.step}
                            initial={{ y: 10, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="rounded-[2rem] bg-white border border-honeySoft p-6 relative"
                        >
                            <span className="absolute -top-3 -left-2 rounded-full bg-brightGreen text-white w-9 h-9 flex items-center justify-center font-display font-bold shadow-md">{w.step}</span>
                            <div className="rounded-2xl bg-honeySoft p-3 inline-block">
                                <IconByName name={w.icon} className="w-6 h-6 text-deepGreen" strokeWidth={2.4} />
                            </div>
                            <p className="font-display text-xl font-bold text-deepGreen mt-4">{w.title}</p>
                            <p className="text-mocha mt-1 text-sm leading-relaxed">{w.desc}</p>
                        </motion.div>
                    ))}
                </div>
                <div className="mt-10 flex justify-center">
                    <BigButton onClick={playFirst} variant="primary" data-testid="walkthrough-cta">
                        <PlayCircle className="w-5 h-5" strokeWidth={2.5} /> Try Poem #01 free — no sign-in
                    </BigButton>
                </div>
            </section>

            {/* Library */}
            <section id="library" className="max-w-6xl mx-auto px-6 pb-16">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <p className="font-display text-sm font-bold text-brightGreen uppercase tracking-wider">The library</p>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-deepGreen mt-1">20 poems for little voices</h2>
                        <p className="text-mocha mt-1">Always free. The first is already open — the rest unlock the moment you sign in.</p>
                    </div>
                    {!user && (
                        <BigButton onClick={() => navigate("/register")} variant="primary" data-testid="library-signin">
                            <LogIn className="w-5 h-5" strokeWidth={2.5} />
                            Unlock all 20
                        </BigButton>
                    )}
                </div>

                {fetching ? (
                    <SkeletonGrid />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="poem-grid">
                        {poems.map((p) => (
                            <PoemTile key={p.poem_id} poem={p} onClick={() => goPoem(p)} lockedLabel={!user ? "Sign in to unlock" : p.lock_reason === "child_required" ? "Add a child" : "Unlocks soon"} />
                        ))}
                    </div>
                )}
            </section>

            {/* Client requirement, 2026-09-25 (Dr. Mohamed Ibrahim, CEO):
                bring this pair back above FAQ (still footer icons too, see
                the Footer section below) — flex-row from the smallest
                breakpoint up (wrapping instead of stacking vertically) and
                a touch less side padding than the rest of the page's
                max-w-6xl sections use. */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
                <div className="flex flex-row flex-wrap gap-3">
                    <div className="flex-1 min-w-[260px]">
                        <LiveClassCard config={liveClass} compact />
                    </div>
                    <div className="flex-1 min-w-[260px]">
                        <WhatsAppCommunityCard />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <p className="font-display text-sm font-bold text-brightGreen uppercase tracking-wider">Questions grown-ups ask</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold text-deepGreen mt-2">Frequently asked questions</h2>
                </div>
                <Accordion type="single" collapsible className="rounded-[2rem] bg-white border border-honeySoft px-2 sm:px-4 divide-y divide-honeySoft" data-testid="faq-list">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`item-${i}`} className="border-0 py-1">
                            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display text-lg font-semibold text-deepGreen py-5 px-2 hover:no-underline">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-mocha leading-relaxed px-2 pb-5 text-base">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>

            {/* Final CTA banner */}
            <section className="max-w-6xl mx-auto px-6 pb-16">
                <div className="rounded-[2.5rem] bg-brightGreen text-white p-10 sm:p-14 relative overflow-hidden line-pattern" data-testid="final-cta">
                    <p aria-hidden className="font-arabic text-white/15 text-[10rem] leading-none absolute -top-6 -right-6 select-none">نور</p>
                    <div className="relative grid md:grid-cols-5 gap-8 items-center">
                        <div className="md:col-span-3 space-y-4">
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">Ready to hear their first rhyme?</h2>
                            <p className="text-white/90 text-lg max-w-lg">The first poem is already unlocked. Create a free account to open all 20, add every child in the family, and save every star.</p>
                        </div>
                        <div className="md:col-span-2 flex flex-wrap gap-3">
                            <button
                                onClick={playFirst}
                                className="rounded-full bg-white text-deepGreen px-7 py-4 font-display font-bold text-lg hover:bg-honeySoft min-h-[56px] shadow-lg shadow-black/10"
                                data-testid="final-cta-play"
                            >
                                <PlayCircle className="inline w-5 h-5 mr-2" strokeWidth={2.5} />
                                Play free poem
                            </button>
                            {!user && (
                                <BigButton onClick={() => navigate("/register")} variant="secondary" data-testid="final-cta-signin">
                                    <LogIn className="w-5 h-5" strokeWidth={2.5} />
                                    Create a free account
                                </BigButton>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Our promise */}
            <section id="promise" className="max-w-6xl mx-auto px-6 pb-20">
                <div className="rounded-[2rem] bg-white border border-honeySoft p-8 sm:p-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <p className="font-display text-sm font-bold text-brightGreen uppercase tracking-wider">Our promise</p>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-deepGreen mt-2">A quiet corner of the internet — just for your child.</h2>
                            <p className="text-mocha mt-4 leading-relaxed">No ads, ever. No public profiles. No comments or messages between children. Every recording your child makes is <strong className="text-deepGreen">private by default</strong> — only you can play it back.</p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <span className="inline-flex items-center gap-2 rounded-full bg-honeySoft px-4 py-1.5 text-sm font-bold text-deepGreen">
                                    <ShieldCheck className="w-4 h-4" strokeWidth={2.8} /> Private by default
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-full bg-honeySoft px-4 py-1.5 text-sm font-bold text-deepGreen">
                                    <Sparkles className="w-4 h-4" strokeWidth={2.8} /> Free for the first 1,000 users
                                </span>
                            </div>
                        </div>
                        <div className="rounded-[1.75rem] bg-lavender p-8 relative overflow-hidden">
                            <p aria-hidden className="font-arabic text-deepGreen text-[6rem] leading-none opacity-30 absolute -top-4 right-4">كتاب</p>
                            <div className="relative">
                                <p className="font-display text-2xl font-bold text-deepGreen">From HikmahVerses</p>
                                <p className="text-mocha mt-2 leading-relaxed">Sister product to Quran Fluency (QFC). Sign in once with Google — your child&apos;s progress will travel between apps.</p>
                                <div className="mt-4 flex items-center gap-2 text-brightGreen font-semibold">
                                    <ChevronRight className="w-4 h-4" strokeWidth={3} /> One family, one login
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t border-honeySoft">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="md:col-span-2">
                            <div className="flex items-center gap-3">
                                <img src="/brand/hikmahverses-mark.png" alt="HikmahVerses" className="h-9 w-auto shrink-0" />
                                <p className="font-display font-bold text-lg text-deepGreen">Little Verses</p>
                            </div>
                            <p className="text-mocha mt-4 text-sm max-w-sm leading-relaxed">A HikmahVerses / Verses Learning Hub product. Twenty poems, ten languages, zero ads. Made with care for little readers.</p>
                        </div>
                        <div>
                            <p className="font-display font-bold text-deepGreen text-sm uppercase tracking-wider mb-3">Explore</p>
                            <ul className="space-y-2 text-sm text-mocha">
                                <li><a href="#library" className="hover:text-brightGreen">All 20 poems</a></li>
                                <li><a href="#how" className="hover:text-brightGreen">How it works</a></li>
                                <li><a href="#faq" className="hover:text-brightGreen">FAQ</a></li>
                            </ul>
                        </div>
                        <div>
                            <p className="font-display font-bold text-deepGreen text-sm uppercase tracking-wider mb-3">Family</p>
                            <ul className="space-y-2 text-sm text-mocha">
                                <li><a href="#promise" className="hover:text-brightGreen">Our promise</a></li>
                                <li><span className="text-lockedText">Quran Fluency (soon)</span></li>
                                <li><span className="text-lockedText">Verses Learning Hub</span></li>
                            </ul>
                        </div>
                    </div>
                    <div className="mt-10 pt-6 border-t border-honeySoft flex flex-wrap items-center justify-between gap-4 text-xs text-mocha">
                        <p>© {new Date().getFullYear()} HikmahVerses · Little Verses. Made with care for little readers.</p>
                        {/* Client requirement, 2026-09-25 (Dr. Mohamed
                            Ibrahim, CEO): "remove live session and community
                            link in the top and bring it as an icon or link
                            in the footer" — this pair used to be a prominent
                            row above the hero headline; now it's two quiet
                            icon links down here instead. Live Class stays a
                            popover (not a direct link) because, unlike the
                            WhatsApp channel, its target URL only exists once
                            a class is actually scheduled — see
                            LiveClassCard's `isReady` check. */}
                        <div className="flex items-center gap-2">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#2D8CFF]/10 text-[#2D8CFF] hover:bg-[#2D8CFF]/20 transition-colors"
                                        aria-label="Live class"
                                        data-testid="footer-live-class-trigger"
                                    >
                                        <Video className="w-4 h-4" strokeWidth={2.5} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-80 p-0 border-honeySoft" data-testid="footer-live-class-popover">
                                    <LiveClassCard config={liveClass} />
                                </PopoverContent>
                            </Popover>
                            <a
                                href={WHATSAPP_COMMUNITY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Join our WhatsApp Community"
                                data-testid="footer-whatsapp-community-link"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition-colors"
                            >
                                <WhatsAppGlyph className="w-4 h-4" />
                            </a>
                        </div>
                        <p className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brightGreen" strokeWidth={2.5} /> Private by default · No ads · No payments</p>
                    </div>
                </div>
            </footer>

            <UnlockCountdownDialog
                open={!!lockedDialog}
                onOpenChange={(open) => !open && setLockedDialog(null)}
                title={lockedDialog?.title}
                subtitle={lockedDialog?.subtitle}
                unlocksAt={lockedDialog?.unlocksAt}
            />
            <WhatsAppButton />

            <GuidedTour steps={landingTourSteps} active={tour.active} onFinish={tour.finish} />

            {/* Testing-only trigger to re-run the tour without clearing
                localStorage by hand. Bottom-left, out of the way of the
                WhatsApp button and install prompt which both live
                bottom-right. */}
            <button
                type="button"
                onClick={tour.replay}
                aria-label="Replay tour"
                className="fixed bottom-4 left-4 z-[10001] inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-honeySoft p-2.5 sm:px-3 sm:py-1.5 text-xs font-semibold text-mocha shadow hover:border-brightGreen hover:text-deepGreen transition"
                data-testid={TOUR.replayButton}
            >
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.4} />
                {/* Text hidden below sm — on a phone the hero's own CTAs can
                    wrap down into this same bottom-corner strip (confirmed
                    live: this button was overlapping "Recordings stay
                    private by default"), so a small icon-only button here
                    has far less chance of sitting on top of real content. */}
                <span className="hidden sm:inline">Replay Tour</span>
            </button>
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-[2rem] bg-white/60 h-[220px] animate-pulse" />
            ))}
        </div>
    );
}
